import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { useAccess } from '../../../hooks/useAccess';
import getTableColumns, { getDefaultSettings } from './helpers/tableColumns';
import { getCellReducers } from './helpers/getCellReducers';
import useFlexibleColumnsTable from '../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../shared/settingsNames';
import {
  useDeleteAppVariable,
  useGetAppVariables,
} from '../../../services/redux/api/appConfigApi';
import { IAppVariable } from '../../../types/interfaces/app-variable';
import { FormikProps, useFormik } from 'formik';
import { AppVariableType } from '../../../shared/constants';

export function useAppVariablesComponent(classes) {
  const { t } = useTranslation();
  const { data: appVariables } = useGetAppVariables();
  const [itemForDelete, setItemForDelete] = useState<IAppVariable>(null);
  const [itemForEditing, setItemForEditing] = useState<IAppVariable>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const { access } = useAccess();

  const [deleteAppVariable] = useDeleteAppVariable();
  const { order: defaultOrder, orderBy } = getDefaultSettings(t);
  const allOption = { id: 'all', name: t('common.all') };
  const filterFormik: FormikProps<any> = useFormik({
    enableReinitialize: true,
    initialValues: {
      type: allOption,
      isPrivate: allOption,
    },
    onSubmit: () => {},
  });

  const filteredValues = useMemo(
    () =>
      appVariables?.filter((variable: IAppVariable) => {
        const typeFilterId = filterFormik.values.type?.id ?? 'all';
        const isPrivateFilterId = filterFormik.values.isPrivate?.id ?? 'all';
        const filterValue = filter.toLowerCase();
        const includesFilter =
          String(variable.key ?? '')
            .toLowerCase()
            .includes(filterValue) ||
          String(variable.value ?? '')
            .toLowerCase()
            .includes(filterValue);

        if (!includesFilter) return false;

        if (typeFilterId !== 'all' && variable.type !== typeFilterId)
          return false;

        if (isPrivateFilterId !== 'all') {
          const isPrivate = !!variable.isPrivate;
          if (
            (isPrivateFilterId === true && !isPrivate) ||
            (isPrivateFilterId === false && isPrivate)
          ) {
            return false;
          }
        }

        return true;
      }),
    [appVariables, filter, filterFormik.values],
  );

  const cellReducers = getCellReducers(
    t,
    classes,
    {
      canEdit: access?.appVariables?.canEdit ?? false,
      canDelete: access?.appVariables?.canDelete ?? false,
    },
    (variable: IAppVariable) => {
      setItemForEditing(variable);
      setShowEditModal(true);
    },
    (variable: IAppVariable) => {
      setItemForDelete(variable);
    },
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.APP_VARIABLE_TABLE_SETTINGS,
    getTableColumns(t),
    orderBy,
    defaultOrder,
  );

  const {
    order,
    columnsListForPopup,
    columnsList,
    onToggleHideColumn,
    onRestoreDefault,
    sortedArray,
    onSortColumns,
    onSort,
  } = useTableData;

  function onItemDelete() {
    setItemForDelete(null);
    deleteAppVariable(itemForDelete.id);
  }

  const filterTypeOptions = [
    allOption,
    ...Object.values(AppVariableType).map((type) => ({ id: type, name: type })),
  ];

  const filterIsPrivateOptions = [
    allOption,
    { id: true, name: t('common.yes') },
    { id: false, name: t('common.no') },
  ];

  return {
    sortedArray,
    columnsList,
    order,
    orderBy,
    onSort,
    onSortColumns,
    cellReducers,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
    filter,
    setFilter,
    showEditModal,
    setShowEditModal,
    itemForEditing,
    setItemForEditing,
    filterFormik,
    filterTypeOptions,
    filterIsPrivateOptions,
  };
}
